import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  LanguageConfig,
  NavigationConfiguration,
  NavigationLink,
  NavigationStateData,
} from '@zipari/shared-ds-util-navigation';
import { BREAKPOINTS } from '../../../../shared/constants';
import { WindowService } from '../../../../shared/services/window.service';
import { ExternalSiteModalConfig } from '../../../external-site-warning-modal/external-site-warning-modal.model';

@Component({
  selector: 'nav-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.scss'],
  // turned off to enable overwriting of icon + link styles
  encapsulation: ViewEncapsulation.None,
})
export class MobileMenuComponent implements OnInit, OnChanges {
  @Input() config: NavigationConfiguration;
  @Input() data: NavigationStateData;
  @Input() externalSiteModalConfig: ExternalSiteModalConfig;
  @Input() activeLangKey: string;

  @Output() contactUsClicked = new EventEmitter<void>();
  @Output() dropdownOptionSelected = new EventEmitter<object>();
  @Output() loginClicked = new EventEmitter<void>();
  @Output() logoutClicked = new EventEmitter<void>();
  @Output() mobileMenuToggled = new EventEmitter<boolean>();
  @Output() navItemClicked = new EventEmitter<NavigationLink>();
  @Output() navMenuToggled = new EventEmitter<NavigationLink>();
  @Output() languageSwitched = new EventEmitter<string>();

  isDropdownOpen: boolean;
  linksConfig: NavigationConfiguration;
  iconData: NavigationStateData;

  constructor(
    private router: Router,
    private window: WindowService,
  ) {}

  @HostListener('window:resize') onResize() {
    const w = this.window.nativeWindow;

    if (this.data.isMobileMenuOpen && w.innerWidth >= BREAKPOINTS.LARGE) {
      this.mobileMenuToggled.emit(false);
    }
  }

  setUp() {
    this.iconData = this.getMenuIcons();
    this.linksConfig = this.getMenuLinks();
  }

  ngOnInit(): void {
    this.setUp();
  }

  getLogOutLabel(): string {
    const profileSettings = this.config?.icons.find(
      (item) => item.key === 'profile_settings',
    );
    const logout = profileSettings?.items.find((item) => item.key === 'logout');

    return logout?.label || 'Log Out';
  }

  ngOnChanges(): void {
    this.setUp();
  }

  getHomeIcon(): string {
    return `home${this.router.url === '/' ? '_active' : ''}`;
  }

  getMenuIcons(): NavigationStateData {
    const iconData = this.data?.iconData
      ? this.data.iconData.filter((icon) => !icon.items)
      : undefined;

    return {
      ...this.data,
      iconData,
    };
  }

  getMenuLinks(): NavigationConfiguration {
    if (!Array.isArray(this.config?.links)) return this.config;

    const links = [...this.config.links];

    if (this.data?.iconData) {
      this.data.iconData
        .filter((icon) => icon.items)
        .forEach((icon) => {
          icon.items.forEach((item) => {
            if (item.route !== '/logout') {
              links.push(item);
            }
          });
        });
    }

    return {
      ...this.config,
      links,
    };
  }

  clickNavItem(navItem: NavigationLink): void {
    if (!(navItem?.items?.length > 0)) {
      this.navItemClicked.emit(navItem);
    } else if (!this.data.openMenuItem) {
      this.data.activeNavItem = navItem;
      this.data.openMenuItem = navItem;
    } else {
      this.data.openMenuItem = null;
      this.data.activeNavItem = null;
    }
  }

  toggleContactUs(): void {
    this.contactUsClicked.emit();
  }

  toggleMobileMenu(isOpen: boolean): void {
    this.mobileMenuToggled.emit(isOpen);
  }

  selectOption(option: object): void {
    this.dropdownOptionSelected.emit(option);
    this.isDropdownOpen = false;
  }

  logout(): void {
    this.logoutClicked.emit();
  }

  login(): void {
    this.loginClicked.emit();
  }

  handleSwitchLanguageClicked(lang: LanguageConfig): void {
    this.languageSwitched.emit(lang.key);
  }
}
